import * as React from "react";
import QrReader from "react-qr-reader";
import { view } from "../../lib/vvm";
import { Layout } from "../../components/Layout/Layout";
import styles from "./EditBoxPage.module.css";
import { EmptyImage } from "../../components/EmptyImage/EmptyImage";
import Button, { ButtonDesign } from "../../components/Button/Button";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import { TypeTagsInput } from "../../components/TypeTagsInput/TypeTagsInput";
import type { Box } from "../../stores/Box";
import {
  EditBoxPageViewModel,
  type EditBoxPageViewProps,
} from "./EditBoxPageViewModel";

interface EditBoxPageProps {
  box: Box;
}

export const EditBoxPage: React.FC<EditBoxPageProps> = view(
  EditBoxPageViewModel,
)<EditBoxPageViewProps>(({
  viewModel: {
    title,
    photo,
    name,
    description,
    typeTags,
    typeTagInput,
    parentId,
    boundQrPayload,
    qrPayloadInput,
    qrBindingError,
    isQrScannerOpen,
    isQrScanResolving,
    qrScannerDelay,
    error,
    referenceError,
    isReferenceLoading,
    isPhotoLoading,
    typeTagSuggestions,
    parentIdSuggestions,
    isSubmitting,
    canSubmit,
    onNameChange,
    onDescriptionChange,
    onPhotoChange,
    onPhotoFileChange,
    setTypeTagInput,
    addTypeTags,
    removeTypeTag,
    onParentIdChange,
    toggleQrScanner,
    onQrScan,
    onQrScanError,
    submit,
    resetFromBox,
    goBack,
    hasPhotoPreview,
  },
}) => {
  return (
    <Layout title={title}>
      <div className={styles.root}>
        {hasPhotoPreview ? (
          <img
            src={photo}
            alt={name || "preview"}
            className={styles.image}
            decoding="async"
          />
        ) : (
          <EmptyImage />
        )}

        <FieldSet title={"наименование"}>
          <Input value={name} onChange={onNameChange} />
        </FieldSet>

        <FieldSet title={"описание"}>
          <Input value={description} onChange={onDescriptionChange} />
        </FieldSet>

        <FieldSet title={"photo (URL)"}>
          <Input
            value={photo}
            onChange={onPhotoChange}
            placeholder="https://..."
          />
          <input
            type="file"
            accept="image/*"
            onChange={(event) => void onPhotoFileChange(event)}
            className={styles.fileInput}
          />
          {isPhotoLoading && (
            <p className={styles.hint}>загружаем изображение...</p>
          )}
        </FieldSet>

        <FieldSet title={"типы (#теги)"}>
          <TypeTagsInput
            tags={typeTags}
            suggestions={typeTagSuggestions}
            inputValue={typeTagInput}
            onInputValueChange={setTypeTagInput}
            onAddTags={addTypeTags}
            onRemoveTag={removeTypeTag}
            datalistId="edit-box-type-tags"
            placeholder="#продукты #быт"
          />
        </FieldSet>

        <FieldSet title={"коробка-родитель (опционально)"}>
          <Input
            value={parentId}
            onChange={onParentIdChange}
            placeholder="Например: Склад"
            list="edit-box-parent-ids"
          />
          <datalist id="edit-box-parent-ids">
            {parentIdSuggestions.map((box) => (
              <option key={box.id} value={box.label} />
            ))}
          </datalist>
        </FieldSet>

        <FieldSet title={"QR-код (опционально)"}>
          <p className={styles.hint}>
            Текущий QR:{" "}
            <strong>{boundQrPayload ? boundQrPayload : "не привязан"}</strong>
          </p>
          <div className={styles.qrControls}>
            <Button
              design={
                isQrScannerOpen ? ButtonDesign.secondary : ButtonDesign.basic
              }
              onClick={toggleQrScanner}
              disabled={isSubmitting || isQrScanResolving}
            >
              {isQrScannerOpen ? "Остановить сканирование" : "Сканировать QR"}
            </Button>
          </div>

          {isQrScannerOpen && (
            <div className={styles.qrScannerWrap}>
              <QrReader
                delay={qrScannerDelay}
                facingMode="environment"
                onError={onQrScanError}
                onScan={(value) => void onQrScan(value)}
                className={styles.qrReader}
                style={{ width: "100%" }}
              />
              <div className={styles.qrOverlayFrame} />
            </div>
          )}

          <p className={styles.hint}>
            {qrPayloadInput
              ? `Новый QR: ${qrPayloadInput}`
              : "Отсканируйте новый QR, если хотите заменить текущую привязку."}
          </p>
          {isQrScanResolving && <p className={styles.hint}>проверяем QR...</p>}
          {qrBindingError && <p className={styles.error}>{qrBindingError}</p>}
        </FieldSet>

        {isReferenceLoading && (
          <p className={styles.hint}>загружаем коробки и теги...</p>
        )}
        {referenceError && <p className={styles.error}>{referenceError}</p>}
        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.buttons}>
          <Button
            design={ButtonDesign.primary}
            onClick={() => void submit()}
            disabled={!canSubmit}
            isLoading={isSubmitting}
          >
            сохранить
          </Button>
          <Button
            design={ButtonDesign.basic}
            onClick={resetFromBox}
            disabled={isSubmitting}
          >
            сбросить
          </Button>
        </div>

        <Button design={ButtonDesign.ghost} onClick={goBack}>
          назад к коробке
        </Button>
      </div>
    </Layout>
  );
});
